/**
 * Analytics and training insights module for LiftForge
 * Contains pure functions for computing workout statistics and progress analysis
 */

import { HistoryEntry, AppState } from '@/lib/types';
import { toDateStr, calcE1RM } from '@/lib/helpers';

/**
 * Mapping of compound exercises to the secondary muscle groups they activate
 * Used for calculating muscle balance and recovery warnings
 */
export const COMPOUND_SECONDARY: Record<string, string[]> = {
  'Flat Barbell Bench Press': ['shoulders', 'triceps'],
  'Incline Barbell Bench Press': ['shoulders', 'triceps'],
  'Decline Barbell Bench Press': ['shoulders', 'triceps'],
  'Flat Dumbbell Press': ['shoulders', 'triceps'],
  'Incline Dumbbell Press': ['shoulders', 'triceps'],
  'Conventional Deadlift': ['back', 'legs', 'core'],
  'Sumo Deadlift': ['back', 'legs', 'core'],
  'Trap Bar Deadlift': ['back', 'legs', 'core'],
  'Barbell Row (Overhand)': ['biceps', 'core'],
  'Barbell Row (Underhand)': ['biceps', 'core'],
  'Pendlay Row': ['biceps', 'core'],
  'Pull-Ups': ['biceps'],
  'Chin-Ups': ['biceps'],
  'Weighted Pull-Ups': ['biceps'],
  'Barbell Overhead Press': ['triceps', 'core'],
  'Dumbbell Overhead Press': ['triceps', 'core'],
  'Back Squat': ['core', 'calves'],
  'Front Squat': ['core'],
  'Chest Dips': ['triceps', 'shoulders'],
  'Dips (Tricep Focus)': ['chest', 'shoulders'],
};

/**
 * Get the Monday (week start) of a given date
 * Calculates by subtracting appropriate days from the given date
 */
export function getWeekStart(d: Date): Date {
  const r = new Date(d);
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  return r;
}

/**
 * Calculate current and best workout streaks
 * Current streak is counted from today or yesterday if today has no workout
 * Best streak is the longest consecutive day streak in history
 */
export interface StreakResult {
  current: number;
  best: number;
}

export function calcStreak(history: HistoryEntry[] | null | undefined): StreakResult {
  if (!history || history.length === 0) return { current: 0, best: 0 };

  const dates = new Set(history.map((h) => h.date));
  const today = new Date();
  const todayStr = toDateStr(today);
  const yest = new Date(today);
  yest.setDate(yest.getDate() - 1);
  const yestStr = toDateStr(yest);

  let current = 0;
  let checkDate = dates.has(todayStr) ? new Date(today) : dates.has(yestStr) ? new Date(yest) : null;

  if (checkDate) {
    while (true) {
      const ds = toDateStr(checkDate);
      if (!dates.has(ds)) break;
      current++;
      checkDate.setDate(checkDate.getDate() - 1);
    }
  }

  let best = 0;
  let run = 1;
  const allSorted = Array.from(dates).sort();
  for (let i = 1; i < allSorted.length; i++) {
    const prev = new Date(allSorted[i - 1] + 'T00:00:00');
    const curr = new Date(allSorted[i] + 'T00:00:00');
    if ((curr.getTime() - prev.getTime()) / 86400000 === 1) {
      run++;
    } else {
      if (run > best) best = run;
      run = 1;
    }
  }
  if (run > best) best = run;
  if (current > best) best = current;

  return { current, best };
}

/**
 * Calculate rolling average of weight over a moving window
 * Default window is 7 days
 */
export interface WeightEntry {
  date: string;
  weight: number;
}

export interface RollingAvgResult {
  date: string;
  avg: number;
}

export function calcRollingAvg(weightLog: WeightEntry[] | null | undefined, window?: number): RollingAvgResult[] {
  window = window || 7;
  if (!weightLog || weightLog.length === 0) return [];

  const result: RollingAvgResult[] = [];
  for (let i = 0; i < weightLog.length; i++) {
    const start = Math.max(0, i - window + 1);
    let sum = 0;
    let count = 0;
    for (let j = start; j <= i; j++) {
      if (weightLog[j].weight) {
        sum += weightLog[j].weight;
        count++;
      }
    }
    result.push({
      date: weightLog[i].date,
      avg: count > 0 ? Math.round((sum / count) * 10) / 10 : 0,
    });
  }
  return result;
}

/**
 * Simple linear regression to find slope and R² of fit
 * Used for trend analysis of volume, progressive overload, etc.
 */
export interface LinearRegressionResult {
  slope: number;
  r2: number;
}

export function linReg(points: number[]): LinearRegressionResult {
  const n = points.length;
  if (n < 2) return { slope: 0, r2: 0 };

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += points[i];
    sumXY += i * points[i];
    sumX2 += i * i;
  }

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, r2: 0 };

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const mean = sumY / n;

  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    ssRes += (points[i] - (intercept + slope * i)) ** 2;
    ssTot += (points[i] - mean) ** 2;
  }

  return {
    slope,
    r2: ssTot === 0 ? 0 : 1 - ssRes / ssTot,
  };
}

/**
 * Recovery warning detection
 * Identifies muscle groups trained with insufficient recovery time (1 day or less)
 * within the last 14 days
 */
export interface RecoveryWarning {
  muscle: string;
  dates: [string, string]; // [earlier_date, later_date]
  gap: number; // days between
}

export function calcRecoveryWarnings(
  history: HistoryEntry[] | null | undefined,
  getCat: (exerciseName: string) => string | null,
): RecoveryWarning[] {
  if (!history || history.length < 3) return [];

  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setDate(today.getDate() - 14);
  const cutoffStr = toDateStr(cutoff);

  const recent = history.filter((h) => h.date >= cutoffStr);
  if (recent.length === 0) return [];

  const muscleHits: Record<string, string[]> = {};

  recent.forEach((h) => {
    const cats = new Set<string>();
    (h.exercises || []).forEach((ex) => {
      const primary = getCat(ex.name);
      if (primary) cats.add(primary);
      const secondaries = COMPOUND_SECONDARY[ex.name];
      if (secondaries) {
        secondaries.forEach((s) => {
          cats.add(s);
        });
      }
    });
    cats.forEach((cat) => {
      if (!muscleHits[cat]) muscleHits[cat] = [];
      muscleHits[cat].push(h.date);
    });
  });

  const warnings: RecoveryWarning[] = [];
  Object.keys(muscleHits).forEach((cat) => {
    const dates = Array.from(new Set(muscleHits[cat])).sort().reverse();
    if (dates.length < 2) return;

    for (let i = 0; i < dates.length - 1; i++) {
      const d1 = new Date(dates[i] + 'T00:00:00');
      const d2 = new Date(dates[i + 1] + 'T00:00:00');
      if ((d1.getTime() - d2.getTime()) / 86400000 <= 1) {
        warnings.push({
          muscle: cat,
          dates: [dates[i + 1], dates[i]],
          gap: (d1.getTime() - d2.getTime()) / 86400000,
        });
        break;
      }
    }
  });

  return warnings;
}

/**
 * Consistency score calculation
 * Tracks actual workouts vs planned workouts over the last 4 weeks
 * Score is 0-100 representing adherence to schedule
 */
export interface WeekCount {
  start: string;
  end: string;
  count: number;
}

export interface ConsistencyScore {
  score: number; // 0-100
  planned: number;
  completed: number;
  weeks: WeekCount[];
  plannedPerWeek: number;
}

export function calcConsistencyScore(history: HistoryEntry[] | null | undefined, scheduleState: Partial<AppState> | null | undefined): ConsistencyScore {
  if (!history || history.length === 0)
    return { score: 0, planned: 0, completed: 0, weeks: [], plannedPerWeek: 0 };

  const today = new Date();
  const weeks: WeekCount[] = [];

  for (let w = 3; w >= 0; w--) {
    const ws = getWeekStart(today);
    ws.setDate(ws.getDate() - w * 7);
    const we = new Date(ws);
    we.setDate(ws.getDate() + 6);

    const startStr = toDateStr(ws);
    const endStr = toDateStr(we);
    const count = new Set(
      history
        .filter((h) => h.date >= startStr && h.date <= endStr)
        .map((h) => h.date),
    ).size;

    weeks.push({ start: startStr, end: endStr, count });
  }

  let plannedPerWeek = 0;
  if (scheduleState) {
    if (scheduleState.scheduleType === 'weekly' && scheduleState.weeklySchedule) {
      plannedPerWeek = scheduleState.weeklySchedule.filter((d) => d.workoutId && d.workoutId !== 'rest').length;
    } else if (scheduleState.scheduleType === 'cadence' && scheduleState.cadenceSchedule) {
      const rot = scheduleState.cadenceSchedule.rotation || [];
      const restEvery = scheduleState.cadenceSchedule.autoRestEvery || 3;
      if (rot.length > 0) {
        plannedPerWeek = Math.round((7 * rot.length) / (rot.length + Math.floor(rot.length / restEvery)));
      }
    }
  }

  if (!plannedPerWeek) {
    const total = weeks.reduce((a, w) => a + w.count, 0);
    plannedPerWeek = Math.max(1, Math.round(total / 4));
  }

  const totalPlanned = plannedPerWeek * 4;
  const totalCompleted = weeks.reduce((a, w) => a + w.count, 0);

  return {
    score: totalPlanned > 0 ? Math.min(100, Math.round((totalCompleted / totalPlanned) * 100)) : 0,
    planned: totalPlanned,
    completed: totalCompleted,
    weeks,
    plannedPerWeek,
  };
}

/**
 * Calculate volume trend over weeks
 * Volume = sum of (weight × reps) for all sets in a week
 * Returns trend direction and R² fit for statistical significance
 */
export interface VolumeTrendResult {
  weeklyVolumes: number[];
  trend: 'increasing' | 'decreasing' | 'neutral';
  slope: number;
  r2: number;
}

export function calcVolumeTrend(history: HistoryEntry[] | null | undefined, weeks?: number): VolumeTrendResult {
  weeks = weeks || 8;
  const today = new Date();
  const weekStart = getWeekStart(today);
  const weeklyVolumes: number[] = [];

  for (let w = weeks - 1; w >= 0; w--) {
    const start = new Date(weekStart);
    start.setDate(weekStart.getDate() - w * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    const startStr = toDateStr(start);
    const endStr = toDateStr(end);
    let vol = 0;

    (history || [])
      .filter((h) => h.date >= startStr && h.date <= endStr)
      .forEach((h) => {
        (h.exercises || []).forEach((ex) => {
          (ex.sets || []).forEach((s) => {
            if (!s.warmup && s.weight > 0 && s.reps > 0) {
              vol += s.weight * s.reps;
            }
          });
        });
      });

    weeklyVolumes.push(vol);
  }

  if (!history || history.length === 0) return { weeklyVolumes, trend: 'neutral', slope: 0, r2: 0 };

  const nonZero = weeklyVolumes.filter((v) => v > 0);
  if (nonZero.length < 2) return { weeklyVolumes, trend: 'neutral', slope: 0, r2: 0 };

  const reg = linReg(nonZero);
  const trend: 'increasing' | 'decreasing' | 'neutral' =
    reg.r2 > 0.3
      ? reg.slope > 0
        ? 'increasing'
        : reg.slope < 0
          ? 'decreasing'
          : 'neutral'
      : 'neutral';

  return { weeklyVolumes, trend, slope: reg.slope, r2: reg.r2 };
}

/**
 * Muscle balance assessment
 * Analyzes push vs pull and upper vs lower body distribution
 * Flags imbalances where one side is >1.8x or <0.55x the other
 */
export interface MuscleImbalance {
  type: 'push-dominant' | 'pull-dominant' | 'upper-dominant' | 'lower-dominant';
  ratio: number;
  detail: string;
}

export interface MuscleBalanceResult {
  groups: Record<string, number>;
  imbalances: MuscleImbalance[];
}

export function calcMuscleBalance(
  history: HistoryEntry[] | null | undefined,
  getCat: (exerciseName: string) => string | null,
  weeks?: number,
): MuscleBalanceResult {
  weeks = weeks || 4;

  if (!history || history.length === 0) return { groups: {}, imbalances: [] };

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - (weeks * 7));
  const cutoffStr = toDateStr(cutoff);

  const recent = history.filter((h) => h.date >= cutoffStr);
  const setsByGroup: Record<string, number> = {};

  recent.forEach((h) => {
    (h.exercises || []).forEach((ex) => {
      const primary = getCat(ex.name);
      const numSets = (ex.sets || []).filter((s) => !s.warmup).length;

      if (primary && numSets > 0) {
        setsByGroup[primary] = (setsByGroup[primary] || 0) + numSets;
      }

      const secondaries = COMPOUND_SECONDARY[ex.name];
      if (secondaries && numSets > 0) {
        secondaries.forEach((cat) => {
          setsByGroup[cat] = (setsByGroup[cat] || 0) + numSets * 0.5;
        });
      }
    });
  });

  const imbalances: MuscleImbalance[] = [];

  // Push vs Pull
  const pushSets = ['chest', 'shoulders', 'triceps'].reduce((s, g) => s + (setsByGroup[g] || 0), 0);
  const pullSets = ['back', 'biceps'].reduce((s, g) => s + (setsByGroup[g] || 0), 0);

  if (pushSets > 0 && pullSets === 0) {
    imbalances.push({
      type: 'push-dominant',
      ratio: 0,
      detail: `No pull exercises in the last ${weeks} weeks`,
    });
  } else if (pullSets > 0 && pushSets === 0) {
    imbalances.push({
      type: 'pull-dominant',
      ratio: 0,
      detail: `No push exercises in the last ${weeks} weeks`,
    });
  } else if (pushSets + pullSets > 0) {
    const ratio = pushSets / pullSets;
    if (ratio > 1.8) {
      imbalances.push({
        type: 'push-dominant',
        ratio: Math.round(ratio * 10) / 10,
        detail: `Push volume is ${Math.round(ratio * 10) / 10}x your pull volume`,
      });
    } else if (ratio < 0.55) {
      imbalances.push({
        type: 'pull-dominant',
        ratio: Math.round((1 / ratio) * 10) / 10,
        detail: `Pull volume is ${Math.round((1 / ratio) * 10) / 10}x your push volume`,
      });
    }
  }

  // Upper vs Lower
  const upperSets = ['chest', 'back', 'shoulders', 'biceps', 'triceps'].reduce(
    (s, g) => s + (setsByGroup[g] || 0),
    0,
  );
  const lowerSets = ['legs', 'calves'].reduce((s, g) => s + (setsByGroup[g] || 0), 0);

  if (upperSets > 0 && lowerSets === 0) {
    imbalances.push({
      type: 'upper-dominant',
      ratio: 0,
      detail: `No lower body exercises in the last ${weeks} weeks`,
    });
  } else if (lowerSets > 0 && upperSets === 0) {
    imbalances.push({
      type: 'lower-dominant',
      ratio: 0,
      detail: `No upper body exercises in the last ${weeks} weeks`,
    });
  } else if (upperSets + lowerSets > 0) {
    const ulRatio = upperSets / lowerSets;
    if (ulRatio > 2.5) {
      imbalances.push({
        type: 'upper-dominant',
        ratio: Math.round(ulRatio * 10) / 10,
        detail: `Upper body gets ${Math.round(ulRatio * 10) / 10}x more volume than lower body`,
      });
    } else if (ulRatio < 0.4) {
      imbalances.push({
        type: 'lower-dominant',
        ratio: Math.round((1 / ulRatio) * 10) / 10,
        detail: `Lower body gets ${Math.round((1 / ulRatio) * 10) / 10}x more volume than upper body`,
      });
    }
  }

  return { groups: setsByGroup, imbalances };
}

/**
 * Progressive overload detection
 * Tracks estimated 1RM per exercise over time to identify progress trends
 */
export interface ProgressiveOverloadExercise {
  name: string;
  sessions: number;
  trend: 'progressing' | 'regressing' | 'neutral';
  slope: number;
  r2: number;
  pctChange: number;
  firstE1RM: number;
  lastE1RM: number;
}

export function calcProgressiveOverload(
  history: HistoryEntry[] | null | undefined,
  getCat?: (exerciseName: string) => string | null,
  weeks?: number,
): ProgressiveOverloadExercise[] {
  weeks = weeks || 6;

  if (!history || history.length === 0) return [];

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - weeks * 7);
  const cutoffStr = toDateStr(cutoff);

  const recent = history.filter((h) => h.date >= cutoffStr);

  const exSessions: Record<string, Array<{ date: string; e1rm: number }>> = {};

  recent.forEach((h) => {
    (h.exercises || []).forEach((ex) => {
      let best = 0;
      (ex.sets || [])
        .filter((s) => !s.warmup)
        .forEach((s) => {
          const e = calcE1RM(s.weight, s.reps);
          if (e > best) best = e;
        });

      if (best > 0) {
        if (!exSessions[ex.name]) exSessions[ex.name] = [];
        exSessions[ex.name].push({ date: h.date, e1rm: best });
      }
    });
  });

  const results: ProgressiveOverloadExercise[] = [];

  Object.keys(exSessions).forEach((name) => {
    const sessions = exSessions[name].sort((a, b) => (a.date < b.date ? -1 : 1));
    if (sessions.length < 2) return;

    const e1rms = sessions.map((s) => s.e1rm);
    const reg = linReg(e1rms);
    const trend: 'progressing' | 'regressing' | 'neutral' =
      reg.r2 > 0.3
        ? reg.slope > 0
          ? 'progressing'
          : 'regressing'
        : 'neutral';

    const first = e1rms[0];
    const last = e1rms[e1rms.length - 1];
    const pctChange = first > 0 ? Math.round(((last - first) / first) * 100) : 0;

    results.push({
      name,
      sessions: sessions.length,
      trend,
      slope: Math.round(reg.slope * 10) / 10,
      r2: Math.round(reg.r2 * 100) / 100,
      pctChange,
      firstE1RM: first,
      lastE1RM: last,
    });
  });

  return results;
}

/**
 * Plateau detection
 * Identifies exercises with 3+ sessions where progress has stalled (< 2% change, neutral trend)
 */
export interface PlateauExercise {
  name: string;
  sessions: number;
  e1rm: number;
  pctChange: number;
}

export function detectPlateaus(
  history: HistoryEntry[] | null | undefined,
  getCat?: (exerciseName: string) => string | null,
  weeks?: number,
): PlateauExercise[] {
  return calcProgressiveOverload(history, getCat, weeks || 8)
    .filter((ex) => ex.sessions >= 3 && Math.abs(ex.pctChange) < 2 && ex.trend === 'neutral')
    .map((ex) => ({
      name: ex.name,
      sessions: ex.sessions,
      e1rm: ex.lastE1RM,
      pctChange: ex.pctChange,
    }));
}

/**
 * Master training insights function
 * Composes all analytics functions to provide comprehensive workout analysis
 */
export interface TrainingInsights {
  recovery: RecoveryWarning[];
  consistency: ConsistencyScore;
  volumeTrend: VolumeTrendResult;
  muscleBalance: MuscleBalanceResult;
  overload: ProgressiveOverloadExercise[];
  plateaus: PlateauExercise[];
  streak: StreakResult;
}

export function calcTrainingInsights(
  history: HistoryEntry[] | null | undefined,
  scheduleState: Partial<AppState> | null | undefined,
  getCat: (exerciseName: string) => string | null,
): TrainingInsights {
  return {
    recovery: calcRecoveryWarnings(history, getCat),
    consistency: calcConsistencyScore(history, scheduleState),
    volumeTrend: calcVolumeTrend(history),
    muscleBalance: calcMuscleBalance(history, getCat),
    overload: calcProgressiveOverload(history, getCat),
    plateaus: detectPlateaus(history, getCat),
    streak: calcStreak(history),
  };
}
